// Side-effect: register Tabulator modules first — `extendModule` below
// silently no-ops if the named module hasn't been registered yet.
import './register';

import { Tabulator } from 'tabulator-tables';

// Custom layout: "contentFill".
// Tabulator's built-in layouts each fall short for our use case:
//   - fitColumns distributes container width equally → narrow content columns
//     get as much space as wide ones
//   - fitData / fitDataFill size to content but don't fill spare space →
//     awkward gap on wide viewports
//   - fitDataStretch stretches only the LAST column, which is usually the
//     action column and doesn't look right
//   - fitDataTable makes the table inline-block and stops scrolling on narrow
//
// contentFill runs in two phases:
//   1. Size each column to its content, then apply a content-measurement cap
//      read from `col.definition.contentCap` (the component controller stashes
//      the caller's `maxWidth` there — see the config getter for why).
//      The cap stops a single outlier value from blowing out a whole column;
//      anything past it truncates with ellipsis.
//   2. If the container has spare width, distribute it proportionally across
//      columns with `widthGrow > 0`. Pinned columns (widthGrow: 0) keep their
//      content-measured width. The cap from phase 1 is NOT re-applied — it's
//      a content cap, not a hard ceiling, so columns stretch freely to fill
//      the viewport rather than leaving a gap.
//
// If content is wider than the container, no fill happens and Tabulator's
// internal `.tabulator-tableholder` scrolls horizontally as usual.
//
// Per-column hints:
//   - `widthGrow: 0` — pin the column to its content width, no stretching
//     (native Tabulator option)
//   - `widthGrow: 2` — take a double share of the spare space
//     (native Tabulator option)
//   - `maxWidth: N`  — cap content measurement at N pixels (default 300).
//     Our component intercepts this and converts it to `contentCap`; Tabulator
//     never sees a native `maxWidth`, so it can't hard-clamp us during fill.
//   - `width: N`     — set an explicit starting width instead of measuring
//     (native Tabulator option)
Tabulator.extendModule('layout', 'modes', {
  contentFill(columns) {
    columns.forEach((col) => col.reinitializeWidth());

    const visible = columns.filter((col) => col.visible);

    // Phase 1: apply our content-measurement cap. We can't use Tabulator's
    // native `maxWidth` column option for this — Tabulator enforces it as a
    // hard ceiling inside `setWidth`, which would stop us stretching past it
    // during the fill phase below. The component controller stashes the
    // caller's `maxWidth` under `contentCap` so Tabulator ignores it and
    // we can apply it ourselves here.
    visible.forEach((col) => {
      const cap = col.definition.contentCap;
      if (cap > 0 && col.getWidth() > cap) {
        col.setWidth(cap);
      }
    });

    // Phase 2: distribute spare container width to growable columns.
    const container = this.table.rowManager.element;
    const slack =
      container.clientWidth -
      visible.reduce((sum, col) => sum + col.getWidth(), 0);

    if (slack <= 0) return;

    const growable = visible.filter(
      (col) => (col.definition.widthGrow ?? 1) > 0
    );
    if (!growable.length) return;

    const totalUnits = growable.reduce(
      (sum, col) => sum + (col.definition.widthGrow ?? 1),
      0
    );
    const unitSize = slack / totalUnits;

    growable.forEach((col) => {
      const units = col.definition.widthGrow ?? 1;
      col.setWidth(col.getWidth() + unitSize * units);
    });
  },
});
